import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Mail, Calendar as CalendarIcon, FileSpreadsheet, StickyNote } from 'lucide-react';
export function ProblemSection() {
  return <section className="py-24 relative overflow-hidden" data-id="element-459">
      <div className="max-w-7xl mx-auto px-6" data-id="element-460">
        <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-461">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-462">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight" data-id="element-463">
              Booking live music still runs on duct tape.
            </h2>
            <p className="text-lg text-textMuted leading-relaxed mb-8" data-id="element-464">
              Too many teams still book shows across spreadsheets, email
              threads, PDFs, calendars, notes, and disconnected tools. Booker
              brings the workflow together — from finding the right venue or
              artist to signing the contract, collecting the deposit, and
              running the show.
            </p>
          </motion.div>

          {/* Visual Content */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative h-[400px] rounded-xl border border-border bg-surface/20 flex flex-col items-center justify-center overflow-hidden" data-id="element-465">
            {/* The Mess */}
            <div className="absolute inset-0 flex items-center justify-center opacity-40" data-id="element-466">
              {/* Connecting Web Lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20" data-id="element-467">
                <path d="M 25% 25% L 75% 33% L 33% 75% L 66% 66% Z" fill="none" stroke="#F5F5F5" strokeWidth="1" strokeDasharray="4 4" data-id="element-468" />
                <path d="M 50% 50% L 25% 25% M 50% 50% L 75% 33% M 50% 50% L 33% 75% M 50% 50% L 66% 66%" fill="none" stroke="#F59E0B" strokeWidth="1" data-id="element-469" />
              </svg>

              <motion.div animate={{
              y: [0, -15, 0],
              rotate: [-5, 5, -5]
            }} transition={{
              repeat: Infinity,
              duration: 4,
              ease: 'easeInOut'
            }} className="absolute top-1/4 left-1/4 p-4 bg-surface border border-border rounded-lg shadow-[0_0_15px_rgba(34,197,94,0.2)]" data-id="element-470">
                <FileSpreadsheet className="w-8 h-8 text-green-500" data-id="element-471" />
              </motion.div>
              <motion.div animate={{
              y: [0, 20, 0],
              rotate: [10, -5, 10]
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'easeInOut'
            }} className="absolute top-1/3 right-1/4 p-4 bg-surface border border-border rounded-lg shadow-[0_0_15px_rgba(59,130,246,0.2)]" data-id="element-472">
                <Mail className="w-8 h-8 text-blue-500" data-id="element-473" />
              </motion.div>
              <motion.div animate={{
              y: [0, -20, 0],
              rotate: [-10, 10, -10]
            }} transition={{
              repeat: Infinity,
              duration: 6,
              ease: 'easeInOut'
            }} className="absolute bottom-1/4 left-1/3 p-4 bg-surface border border-border rounded-lg shadow-[0_0_15px_rgba(239,68,68,0.2)]" data-id="element-474">
                <FileText className="w-8 h-8 text-red-500" data-id="element-475" />
              </motion.div>
              <motion.div animate={{
              y: [0, 15, 0],
              rotate: [5, -10, 5]
            }} transition={{
              repeat: Infinity,
              duration: 4.5,
              ease: 'easeInOut'
            }} className="absolute bottom-1/3 right-1/3 p-4 bg-surface border border-border rounded-lg shadow-[0_0_15px_rgba(168,85,247,0.2)]" data-id="element-476">
                <CalendarIcon className="w-8 h-8 text-purple-500" data-id="element-477" />
              </motion.div>
              <motion.div animate={{
              y: [0, -10, 0],
              rotate: [-15, 0, -15]
            }} transition={{
              repeat: Infinity,
              duration: 5.5,
              ease: 'easeInOut'
            }} className="absolute top-1/2 left-1/2 p-4 bg-surface border border-border rounded-lg shadow-[0_0_15px_rgba(234,179,8,0.2)]" data-id="element-478">
                <StickyNote className="w-8 h-8 text-yellow-500" data-id="element-479" />
              </motion.div>
            </div>

            {/* The Solution Overlay */}
            <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            type: 'spring',
            bounce: 0.4
          }} className="relative z-10 w-3/4 glow-border-wrapper shadow-2xl" data-id="element-480">
              <div className="glow-border-content bg-background/90 backdrop-blur-xl p-6 text-center rounded-[inherit]" data-id="element-481">
                <div className="flex items-center justify-center gap-2 mb-4" data-id="element-482">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" data-id="element-483"></div>
                  <span className="font-bold text-xl tracking-tight gradient-text" data-id="element-484">
                    Booker
                  </span>
                </div>
                <div className="h-2 w-full bg-surface rounded-full mb-3 overflow-hidden" data-id="element-485">
                  <motion.div initial={{
                  width: 0
                }} whileInView={{
                  width: '100%'
                }} transition={{
                  delay: 0.8,
                  duration: 1.5,
                  ease: 'easeOut'
                }} className="h-full bg-primary" data-id="element-486"></motion.div>
                </div>
                <p className="text-xs text-textMuted font-medium uppercase tracking-wider" data-id="element-487">
                  One Unified Workflow
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}